import { useTranslation } from 'react-i18next';
import type { CallStatus } from '@/types/api';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

const FAILURE: CallStatus[] = ['download_failed', 'transcribe_failed', 'jira_failed'];
const IN_FLIGHT: CallStatus[] = [
  'downloading',
  'transcribing',
  'titling',
  'sending_jira',
  'claiming_download',
  'claiming_transcribe',
  'claiming_title',
  'claiming_jira',
];
const SUCCESS: CallStatus[] = ['finished'];

function styleFor(status: CallStatus | string): string {
  if ((FAILURE as readonly string[]).includes(status)) {
    return 'border-destructive/40 bg-destructive/10 text-destructive';
  }
  if ((SUCCESS as readonly string[]).includes(status)) {
    return 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400';
  }
  if ((IN_FLIGHT as readonly string[]).includes(status)) {
    return 'border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400';
  }
  return 'border-border bg-muted text-muted-foreground';
}

export default function StatusBadge({
  status,
  className,
}: {
  status: CallStatus | string;
  className?: string;
}) {
  const { t } = useTranslation('statuses');
  const label = t(`labels.${status}`, { defaultValue: status });
  const tip = t(`tips.${status}`, { defaultValue: '' });

  const pill = (
    <span
      className={cn(
        'inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium',
        styleFor(status),
        className,
      )}
    >
      {label}
    </span>
  );

  if (!tip) return pill;

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span tabIndex={0}>{pill}</span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-xs">{tip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
